/**
 * ``useApplyProfile`` — the shared "apply a Profile to the live configuration"
 * machinery (REQ-UI-073 / 075; ADR-033 §4.2).
 *
 * # Why a shared hook
 *
 * Applying a Profile is more than a working-state write: it copies the Profile
 * document into the working state **by value**, stamps the ``active_profile``
 * pointer (with the content hash that drives "(modified)" — REQ-UI-074), and
 * imperatively restores the captured **panel layout** to the live dockview
 * workspace (``fromJSON``) — a layout-less Profile snaps to the built-in
 * default. The map camera re-flies to the Profile's ``map.home`` via the
 * working-state effect in :file:`map/Map.tsx`.
 *
 * Two surfaces apply a Profile and must do it identically:
 *   - the **gear → Profiles submenu** Apply action (Flow U), and
 *   - the **top-bar Profile switcher** quick-switch (item 3, 2026-06-12).
 *
 * Before the switcher this logic lived inline in the gear menu; extracting it
 * keeps the two call sites byte-for-byte consistent (no duplicated apply path —
 * CLAUDE.md §4.4). The hook owns only the apply mechanics; the caller owns its
 * own busy / error / close UX (the menu closes its popover; the switcher closes
 * its dropdown).
 *
 * Cross-references:
 *   - REQ-UI-073 / 075 (Apply copies by value + applies all settings live,
 *     including layout); REQ-UI-074 (active-Profile + "(modified)" baseline).
 *   - ADR-033 §4.2 (apply-by-value); ADR-024 §6 (layout ``fromJSON`` restore).
 *   - preferences/preferences-menu.tsx + chrome/top-bar.tsx (the callers).
 */
import { useCallback } from "react";
import type { ActiveProfileRef } from "@/auth/session-types";
import { useMultiPaneApi } from "@/multi-pane/multi-pane-context";
import { usePreferences } from "@/preferences/preferences-context";
import { sha256CanonicalJson } from "@/profiles/profile-hash";
import { useProfiles } from "@/profiles/profiles-context";

/** The discriminated result of an apply (never throws). ``status === "error"``
 *  carries generic, user-facing copy (REQ-UI-037) the caller renders inline. */
export type ApplyProfileResult = { status: "ok" } | { status: "error"; message: string };

/** The hook's surface — a single apply-by-id action. */
export interface UseApplyProfile {
  /**
   * Apply the Profile with ``profileId`` to the live configuration: fetch its
   * document by id, copy it into the working state by value, stamp the
   * ``active_profile`` pointer (content hash → "(modified)" baseline), PUT, and
   * restore its captured panel layout to the live dockview (``applyLayout``) —
   * or snap to the built-in default when the Profile carries no layout. Returns
   * a discriminated result; never throws. A 404 (the Profile vanished / access
   * lost between list and apply) maps to a generic "no longer available".
   */
  applyProfile: (profileId: string) => Promise<ApplyProfileResult>;
}

/**
 * The shared apply-Profile hook. Reads the live preferences writer, the
 * Profiles list (for ``getDocument``), and the multi-pane API (for the layout
 * restore). Safe to call from any surface under the providers (the gear menu
 * and the top bar both mount beneath them).
 */
export function useApplyProfile(): UseApplyProfile {
  const { applyWorkingState } = usePreferences();
  const { getDocument } = useProfiles();
  const multiPaneApi = useMultiPaneApi();

  const applyProfile = useCallback(
    async (profileId: string): Promise<ApplyProfileResult> => {
      try {
        const detail = await getDocument(profileId);
        const doc = detail.document;
        const ref: ActiveProfileRef = {
          scope: detail.scope,
          profile_id: detail.profile_id,
          name: detail.name,
          applied_at: new Date().toISOString(),
          applied_document_sha256: await sha256CanonicalJson(doc),
        };
        const result = await applyWorkingState(doc, ref);
        if (result.status === "error") {
          return { status: "error", message: result.message };
        }
        // Apply the Profile's captured panel layout to the LIVE dockview.
        // ``applyWorkingState`` updates ``working_state`` (incl. its ``layout``
        // slice), but the workspace applies layout IMPERATIVELY (``fromJSON``),
        // not reactively — so without this the previously-active Profile's
        // arrangement would linger after a switch (REQ-UI-075). A layout-less
        // document snaps to the built-in default; ``applyLayout`` role-gates +
        // corrupt-falls-back, so a stale blob is safe. A no-op without a mounted
        // workspace (e.g. a role with no dockview).
        if (multiPaneApi !== null) {
          if (doc.layout !== undefined) {
            multiPaneApi.applyLayout(doc.layout);
          } else {
            multiPaneApi.resetLayout();
          }
        }
        return { status: "ok" };
      } catch {
        // getDocument threw (404 — the Profile vanished or access was lost
        // between list and apply). Generic copy per REQ-UI-037; the list
        // self-corrects on the next refresh.
        return { status: "error", message: "That profile is no longer available." };
      }
    },
    [getDocument, applyWorkingState, multiPaneApi],
  );

  return { applyProfile };
}
